import React, { useState, useEffect } from 'react';
import {
  Search, Building, User, Mail, Calendar, ChevronRight,
  Filter, Users, Pencil, X, CheckCircle, Clock, AlertCircle,
  Download, Send, Eye, Zap, TrendingUp, Star, Activity, CheckCircle2, FileText,
  FileSignature, ClipboardCheck, ScrollText, Settings, History as HistoryIcon
} from 'lucide-react';
import connectionService from '../services/connectionService';
import QuoteProgressService from '../services/quoteProgressService';
import CustomerForm from './AddConnection';
import StepComponent from './StepComponent';
import FibreContractForm from './FibreContractForm';
import TelecomQuotation from './TelecomQuotation';
import RentalAgreementTab from './RentalAgreementTab';
import RentalAgreementForm from './RentalDocumentForm';
import AssignmentLetterTab from './AssignmentLetterTab';
import CertificateOfAcceptanceTab from './CoaTab';
import SystemConfigView from './SystemConfigView';
import LogsTab from './LogsTab';

const ContractViewComponent = ({ onConnectionSelect, onClose }) => {
  const [connections, setConnections] = useState([]);
  const [filteredConnections, setFilteredConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); 
  const [loading, setLoading] = useState(true);
  
  const [currentUser, setCurrentUser] = useState({ id: null, name: '', role: '' });
  const [showModal, setShowModal] = useState(true);
  const [connectionProgress, setConnectionProgress] = useState({});
  const [progressData, setProgressData] = useState({});
  
  // 🔹 Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedStep, setSelectedStep] = useState({ connectionId: null, stepName: null });
  const userRole = localStorage.getItem('Role');
  const isAdmin = userRole === 'Admin' || userRole === 'Support';

  // 🔹 Edit modal state
  const [editingConnection, setEditingConnection] = useState(null);
  const [formData, setFormData] = useState({});

  // NEW STATE: To view contract PDF
  const [viewingContract, setViewingContract] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // NEW STATE: To view quote
  const [viewingQuote, setViewingQuote] = useState(false);
  const [selectedQuoteConnectionId, setSelectedQuoteConnectionId] = useState(null);

  // NEW STATE: To view rental agreement (RentalAgreementTab)
  const [viewingRental, setViewingRental] = useState(false);
  const [selectedRentalConnectionId, setSelectedRentalConnectionId] = useState(null);

  // NEW STATE: To view rental document (RentalAgreementForm)
  const [viewingRentalDoc, setViewingRentalDoc] = useState(false);
  const [selectedRentalDocConnectionId, setSelectedRentalDocConnectionId] = useState(null);

  // NEW STATE: To view assignment letter
  const [viewingAssignment, setViewingAssignment] = useState(false);
  const [selectedAssignmentConnectionId, setSelectedAssignmentConnectionId] = useState(null);

  // NEW STATE: To view certificate of acceptance
  const [viewingCOA, setViewingCOA] = useState(false);
  const [selectedCOAConnectionId, setSelectedCOAConnectionId] = useState(null);

  // NEW STATE: To view system config (read-only)
  const [viewingSystemConfig, setViewingSystemConfig] = useState(false);
  const [selectedSystemConfigConnectionId, setSelectedSystemConfigConnectionId] = useState(null);
  // NEW STATE: To view logs
  const [viewingLogs, setViewingLogs] = useState(false);
  const [selectedLogsConnectionId, setSelectedLogsConnectionId] = useState(null);
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');
    const userRole = localStorage.getItem('Role');

    setCurrentUser({
      id: userId,
      name: userName,
      role: userRole
    });
  }, []);

  // Dynamic step configuration based on your database structure
  const STEP_SEQUENCE = [
    { 
      name: 'system_config', 
      dbName: 'system_config',
      label: 'System Config', 
      color: 'blue',
      icon: <Zap className="h-3 w-3" />,
      gradient: 'from-blue-400 to-blue-600',
      isInitial: true,
      stepType: 'base'
    },
    { 
      name: 'quote-downloaded', 
      dbName: 'quote',
      actionType: 'downloaded',
      label: 'Quote Downloaded', 
      color: 'orange',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-orange-400 to-orange-600',
      stepType: 'action'
    },
    { 
      name: 'quote-emailed', 
      dbName: 'quote',
      actionType: 'emailed',
      label: 'Quote Sent', 
      color: 'orange',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-orange-500 to-orange-700',
      stepType: 'action'
    },
    { 
      name: 'contract-downloaded', 
      dbName: 'contract',
      actionType: 'downloaded',
      label: 'Contract Downloaded', 
      color: 'red',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-red-400 to-red-600',
      stepType: 'action'
    },
    { 
      name: 'contract-emailed', 
      dbName: 'contract',
      actionType: 'emailed',
      label: 'Contract Sent', 
      color: 'red',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-red-500 to-red-700',
      stepType: 'action'
    },
    { 
      name: 'rental-downloaded', 
      dbName: 'rental',
      actionType: 'downloaded',
      label: 'Rental Downloaded', 
      color: 'green',
      icon: <Download className="h-3 w-3" />,
      gradient: 'from-green-400 to-green-600',
      stepType: 'action'
    },
    { 
      name: 'rental-emailed', 
      dbName: 'rental',
      actionType: 'emailed',
      label: 'Rental Sent', 
      color: 'green',
      icon: <Send className="h-3 w-3" />,
      gradient: 'from-green-500 to-green-700',
      stepType: 'action'
    }
  ];

  const loadData = async () => {
    try {
      const connectionsResponse = await connectionService.getContractConnections();
      let connectionsData = Array.isArray(connectionsResponse?.data) 
        ? connectionsResponse.data 
        : [];

      
      setConnections(connectionsData);
      setFilteredConnections(connectionsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setConnections([]);
      setFilteredConnections([]);
    }
  };

  useEffect(() => {
    if (currentUser.id) {
      const loadConnections = async () => {
        setLoading(true);
        await loadData();
        setLoading(false);
      };
      loadConnections();
    }
  }, [currentUser]);

  // FIXED: Map step names to their main categories for redirection
  const getMainStepFromStepName = (stepName) => {
    if (stepName.includes('rental')) return 'rental';
    if (stepName.includes('contract')) return 'contract';
    if (stepName.includes('quote')) return 'quote';
    
    return stepName; // For system_config and others
  };

  // FIXED: Update function to accept object parameter
  const handleConnectionSelect = (params) => {
    // Handle both object parameter and separate parameters
    let connection, stepName, isAdminParam;
    
    if (params && params.connection) {
      // Object parameter style
      connection = params.connection;
      stepName = params.stepName;
      isAdminParam = params.isAdmin;
    } else {
      // For backward compatibility - use params as connection
      connection = params;
      stepName = null;
      isAdminParam = isAdmin; // Use the component's isAdmin state
    }

    let stepToOpen = 'system_config'; // Default step

    if (isAdminParam) {
      if (stepName) {
        // If a specific step was clicked (from progress indicators)
        stepToOpen = getMainStepFromStepName(stepName);
      } else {
        // For regular connection click by admin, use the current progress step
        const progress = getProgressLevel(connection.id);
        stepToOpen = getMainStepFromStepName(progress.currentStep);
        
        // If it's showing "All Steps Completed", go to the last step (rental)
        if (progress.stepLabel === 'All Steps Completed') {
          stepToOpen = 'rental';
        }
      }
    } else {
      // For non-admin users, always go to system_config
      stepToOpen = 'system_config';
    }

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, stepToOpen);
    } else {
      console.error('onConnectionSelect is not a function');
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // NEW FUNCTION: View contract for a specific connection
  const handleViewContract = (connection) => {
    setSelectedContractId(connection.id);
    setViewingContract(true);
    
    // Optional: You can call the parent's onConnectionSelect if needed
    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'contract');
    }
  };

  // NEW FUNCTION: Go back to connection list
  const handleBackToList = () => {
    setViewingContract(false);
    setSelectedContractId(null);
  };

  // NEW FUNCTION: View quote for a specific connection
  const handleViewQuote = (connection) => {
    setSelectedQuoteConnectionId(connection.id);
    setViewingQuote(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'quote');
    }
  };

  // NEW FUNCTION: Go back to connection list from quote view
  const handleBackFromQuote = () => {
    setViewingQuote(false);
    setSelectedQuoteConnectionId(null);
  };

  // NEW FUNCTION: View rental agreement (tab version) for a specific connection
  const handleViewRental = (connection) => {
    setSelectedRentalConnectionId(connection.id);
    setViewingRental(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'rental');
    }
  };

  // NEW FUNCTION: Go back to connection list from rental view
  const handleBackFromRental = () => {
    setViewingRental(false);
    setSelectedRentalConnectionId(null);
  };

  // NEW FUNCTION: View rental document (form version) for a specific connection
  const handleViewRentalDoc = (connection) => {
    setSelectedRentalDocConnectionId(connection.id);
    setViewingRentalDoc(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'rental-doc');
    }
  };

  // NEW FUNCTION: Go back to connection list from rental document view
  const handleBackFromRentalDoc = () => {
    setViewingRentalDoc(false);
    setSelectedRentalDocConnectionId(null);
  };

  // NEW FUNCTION: View assignment letter for a specific connection
  const handleViewAssignment = (connection) => {
    setSelectedAssignmentConnectionId(connection.id);
    setViewingAssignment(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'assignment');
    }
  };

  // NEW FUNCTION: Go back to connection list from assignment view
  const handleBackFromAssignment = () => {
    setViewingAssignment(false);
    setSelectedAssignmentConnectionId(null);
  };

  // NEW FUNCTION: View certificate of acceptance for a specific connection
  const handleViewCOA = (connection) => {
    setSelectedCOAConnectionId(connection.id);
    setViewingCOA(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'coa');
    }
  };

  // NEW FUNCTION: Go back to connection list from COA view
  const handleBackFromCOA = () => {
    setViewingCOA(false);
    setSelectedCOAConnectionId(null);
  };

  // NEW FUNCTION: View system config (read-only) for a specific connection
  const handleViewSystemConfig = (connection) => {
    setSelectedSystemConfigConnectionId(connection.id);
    setViewingSystemConfig(true);

    if (typeof onConnectionSelect === 'function') {
      onConnectionSelect(connection, 'system_config');
    }
  };

  // NEW FUNCTION: Go back to connection list from system config view
  const handleBackFromSystemConfig = () => {
    setViewingSystemConfig(false);
    setSelectedSystemConfigConnectionId(null);
  };

  // FIXED: Handle step click from progress indicators
  const handleStepClick = (connection, stepName) => {
    setSelectedStep({ connectionId: connection.id, stepName: stepName });
    
    // Map to main step category before redirecting
    const mainStep = getMainStepFromStepName(stepName);
    handleConnectionSelect({ 
      connection, 
      stepName: mainStep, 
      isAdmin: isAdmin 
    });
  };

  const handleViewLogs = (connection) => {
  setSelectedLogsConnectionId(connection.id);
  setViewingLogs(true);

  if (typeof onConnectionSelect === 'function') {
    onConnectionSelect(connection, 'logs');
  }
};

const handleBackFromLogs = () => {
  setViewingLogs(false);
  setSelectedLogsConnectionId(null);
};

  // Enhanced step completion check that matches your database structure
  const isActionStepCompleted = (step, progress) => {
    if (step.stepType === 'base') {
      // For system_config, check if it exists in progress data
      const baseProgress = progress.find(p => p.step_name === step.dbName);
      return baseProgress?.completed || false;
    }
    
    // For action steps, find the step by dbName and check the specific action
    const stepProgress = progress.find(p => p.step_name === step.dbName);
    if (!stepProgress) return false;
    
    // Check if the step is completed AND the specific action is true
    return stepProgress.completed && stepProgress.actions?.[step.actionType] === true;
  };

  // Get the display name for a step based on progress data
  const getStepDisplayName = (step, progress) => {
    if (step.stepType === 'base') {
      return step.label;
    }
    
    const stepProgress = progress.find(p => p.step_name === step.dbName);
    if (!stepProgress) return step.label;
    
    // Return dynamic label based on completed actions
    const hasDownloaded = stepProgress.actions?.downloaded;
    const hasEmailed = stepProgress.actions?.emailed;
    
    if (step.dbName === 'quote') {
      if (hasDownloaded && hasEmailed) return 'Quote Downloaded & Sent';
      if (hasDownloaded) return 'Quote Downloaded';
      if (hasEmailed) return 'Quote Sent';
    }
    if (step.dbName === 'contract') {
      if (hasDownloaded && hasEmailed) return 'Contract Downloaded & Sent';
      if (hasDownloaded) return 'Contract Downloaded';
      if (hasEmailed) return 'Contract Sent';
    }
    if (step.dbName === 'rental') {
      if (hasDownloaded && hasEmailed) return 'Rental Downloaded & Sent';
      if (hasDownloaded) return 'Rental Downloaded';
      if (hasEmailed) return 'Rental Sent';
    }
    
    return step.label;
  };

  // FIXED: Enhanced progress calculation to correctly identify current step
  const getProgressLevel = (connectionId) => {
    const progress = progressData[connectionId] || [];
    const totalSteps = STEP_SEQUENCE.length;
    // Track completion for each step with dynamic labels
    const stepDetails = STEP_SEQUENCE.map(step => {
      const completed = isActionStepCompleted(step, progress);
      const displayLabel = getStepDisplayName(step, progress);
      
      return {
        ...step,
        completed,
        displayLabel,
        hasActivity: completed,
        // Store action status for display
        actions: step.stepType === 'action' ? 
          progress.find(p => p.step_name === step.dbName)?.actions || {} 
          : {}
      };
    });

    // Check if this is a brand new connection (no progress data at all)
    const isBrandNew = progress.length === 0;

    // For brand new connections, they start at System Config
    if (isBrandNew) {
      return {
        level: 'system_config',
        color: 'blue',
        percentage: 0,
        currentStep: 'system_config',
        stepLabel: 'System Config',
        stepIndex: 0,
        completedSteps: 0,
        hasActivity: false,
        isFullyComplete: false,
        isBrandNew: true,
        stepDetails: stepDetails
      };
    }

    // FIXED: Find the CURRENT step - the highest completed step
    let currentStepIndex = 0;
    let currentStepName = 'system_config';
    let currentStepLabel = 'System Config';
    let currentStepColor = 'blue';

    // Find the highest completed step
    let highestCompletedIndex = -1;
    for (let i = STEP_SEQUENCE.length - 1; i >= 0; i--) {
      if (stepDetails[i].completed) {
        highestCompletedIndex = i;
        break;
      }
    }

    if (highestCompletedIndex === STEP_SEQUENCE.length - 1) {
      // All steps completed
      currentStepIndex = STEP_SEQUENCE.length - 1;
      currentStepName = STEP_SEQUENCE[currentStepIndex].name;
      currentStepLabel = 'All Steps Completed';
      currentStepColor = 'green';
    } else if (highestCompletedIndex >= 0) {
      // Some steps completed - current step is the highest completed one
      currentStepIndex = highestCompletedIndex;
      currentStepName = STEP_SEQUENCE[currentStepIndex].name;
      currentStepLabel = stepDetails[currentStepIndex].displayLabel;
      currentStepColor = STEP_SEQUENCE[currentStepIndex].color;
    } else {
      // No steps completed yet - at system config
      currentStepIndex = 0;
      currentStepName = 'system_config';
      currentStepLabel = 'System Config';
      currentStepColor = 'blue';
    }

    // Calculate overall progress
    const completedSteps = stepDetails.filter(step => step.completed).length;
    const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

    return {
      level: currentStepName,
      color: currentStepColor,
      percentage: progressPercentage,
      currentStep: currentStepName,
      stepLabel: currentStepLabel,
      stepIndex: currentStepIndex,
      completedSteps: completedSteps,
      hasActivity: stepDetails[currentStepIndex]?.completed || false,
      isFullyComplete: completedSteps === totalSteps,
      isBrandNew: false,
      stepDetails: stepDetails
    };
  };

  const getProgressIcon = (progress) => {
    if (progress.isFullyComplete) return <CheckCircle2 className="h-4 w-4" />;
    if (progress.hasActivity || progress.percentage > 0) return <Activity className="h-4 w-4 animate-pulse" />;
    return <Zap className="h-4 w-4" />;
  };

  // Get status text for tooltip and display
  const getStepStatusText = (step, stepIndex, progress) => {
    if (step.completed) {
      return 'Completed';
    }
    
    if (stepIndex === progress.stepIndex) return 'Current Step';
    if (stepIndex < progress.stepIndex) return 'Completed';
    
    return 'Pending';
  };

  // Get status icon for each step
  const getStepStatusIcon = (step, stepIndex, progress) => {
    if (step.completed) {
      return <CheckCircle2 className="h-3 w-3 text-white" />;
    }
    
    if (stepIndex === progress.stepIndex) {
      return <Clock className="h-3 w-3 text-white" />;
    }
    
    return null;
  };

  // Get dot color based on step status
  const getStepDotColor = (step, stepIndex, progress, stepColors) => {
    if (step.completed) {
      return stepColors.tab + ' shadow-md';
    }
    
    if (stepIndex === progress.stepIndex) {
      return 'bg-blue-400 animate-pulse ring-2 ring-blue-200';
    }
    
    return 'bg-gray-200';
  };

  const getProgressColors = (color, progress) => {
    const baseColors = {
      gray: {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-200 hover:border-gray-300',
        icon: 'text-gray-500',
        tab: 'bg-gradient-to-r from-gray-300 to-gray-400',
        glow: 'shadow-gray-200/50'
      },
      blue: {
        bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
        text: 'text-blue-800',
        border: 'border-blue-200 hover:border-blue-400',
        icon: 'text-blue-600',
        tab: 'bg-gradient-to-r from-blue-400 to-cyan-500',
        glow: 'shadow-blue-200/50'
      },
      red: {
        bg: 'bg-gradient-to-br from-red-50 to-pink-50',
        text: 'text-red-800',
        border: 'border-red-200 hover:border-red-400',
        icon: 'text-red-600',
        tab: 'bg-gradient-to-r from-red-400 to-pink-500',
        glow: 'shadow-red-200/50'
      },
      orange: {
        bg: 'bg-gradient-to-br from-orange-50 to-yellow-50',
        text: 'text-orange-800',
        border: 'border-orange-200 hover:border-orange-400',
        icon: 'text-orange-600',
        tab: 'bg-gradient-to-r from-orange-400 to-yellow-500',
        glow: 'shadow-orange-200/50'
      },
      yellow: {
        bg: 'bg-gradient-to-br from-yellow-50 to-amber-50',
        text: 'text-yellow-800',
        border: 'border-yellow-200 hover:border-yellow-400',
        icon: 'text-yellow-600',
        tab: 'bg-gradient-to-r from-yellow-400 to-amber-500',
        glow: 'shadow-yellow-200/50'
      },
      green: {
        bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
        text: 'text-green-800',
        border: 'border-green-200 hover:border-green-400',
        icon: 'text-green-600',
        tab: 'bg-gradient-to-r from-green-400 to-emerald-500',
        glow: 'shadow-green-200/50'
      }
    };
    
    return baseColors[color] || baseColors.gray;
  };

  useEffect(() => {
    const loadBatchProgress = async () => {
      if (!connections.length) return;
      try {
        const ids = connections.map(c => c.id);
        const progressData = await QuoteProgressService.getProgressBatch(ids);
        //console.log("Progress data", progressData);
        setProgressData(progressData);
      } catch (err) {
        console.error('Failed to load batch progress:', err);
      }
    };

    loadBatchProgress();
  }, [connections]);

  useEffect(() => {
    let filtered = connections;

    if (searchTerm.trim() !== '') {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = connections.filter(connection => {
        return (
          (connection.company_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.salesman_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.md_name?.toLowerCase() || '').includes(lowerSearch) ||
          (connection.company_reg?.toLowerCase() || '').includes(lowerSearch)
        );
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.contract_date) - new Date(a.contract_date);
        case 'salesman':
          return (a.salesman_name || '').localeCompare(b.salesman_name || '');
        case 'company':
          return (a.company_name || '').localeCompare(b.company_name || '');
        default:
          return 0;
      }
    });

    setFilteredConnections(filtered);
    setCurrentPage(1); // reset to first page on new filter/sort
  }, [searchTerm, sortBy, connections]);

  // 🔹 Pagination logic
  const totalPages = Math.ceil(filteredConnections.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentConnections = filteredConnections.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 🔹 Edit handlers
  const handleEditClick = (connection) => {
    setEditingConnection(connection);
    setFormData(connection);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await connectionService.updateConnection(editingConnection.id, formData);
      await loadData();
      setEditingConnection(null);
    } catch (error) {
      console.error("Error updating connection:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Shared "back to list" header used by every full-page document view below
  const ViewHeader = ({ onBack, title, subtitle }) => (
    <div className="flex justify-between items-center mb-6">
      <button
        onClick={onBack}
        className="group flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-200 border border-transparent hover:border-blue-200 shadow-sm hover:shadow-md"
      >
        <ChevronRight className="h-5 w-5 rotate-180 group-hover:-translate-x-1 transition-transform duration-200" />
        <span className="font-medium">Back to Connections</span>
      </button>

      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>
    </div>
  );

  // If viewing contract, render the contract form in view-only mode
  if (viewingContract && selectedContractId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackToList}
          title="Contract View Mode"
          subtitle="Read-only view of contract details"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <FibreContractForm 
            selectedConnectionId={selectedContractId}
            isViewOnly={true}
          />
        </div>
      </div>
    );
  }

  // If viewing quote, render the quotation view
  if (viewingQuote && selectedQuoteConnectionId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackFromQuote}
          title="Quote View"
          subtitle="Quotation details for this connection"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <TelecomQuotation
            selectedConnectionId={selectedQuoteConnectionId}
          />
        </div>
      </div>
    );
  }

  // If viewing rental agreement (tab version), render RentalAgreementTab
if (viewingRental && selectedRentalConnectionId) {
  return (
    <div className="max-w-6xl mx-auto p-6">
      <ViewHeader
        onBack={handleBackFromRental}
        title="Rental Agreement"
        subtitle="Phone system rental agreement for this connection"
      />
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <RentalAgreementTab
          selectedConnectionId={selectedRentalConnectionId}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
}

  // If viewing rental document (form version), render RentalAgreementForm
  if (viewingRentalDoc && selectedRentalDocConnectionId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackFromRentalDoc}
          title="Rental Document"
          subtitle="Universal rental document for this connection"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <RentalAgreementForm
            selectedConnectionId={selectedRentalDocConnectionId}
          />
        </div>
      </div>
    );
  }

  // If viewing assignment letter, render the assignment letter tab
  if (viewingAssignment && selectedAssignmentConnectionId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackFromAssignment}
          title="Assignment Letter"
          subtitle="CF Corporate Finance assignment letter package"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <AssignmentLetterTab
            selectedConnectionId={selectedAssignmentConnectionId}
          />
        </div>
      </div>
    );
  }

  // If viewing certificate of acceptance, render the COA tab
  if (viewingCOA && selectedCOAConnectionId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackFromCOA}
          title="Certificate of Acceptance"
          subtitle="Equipment acceptance certificate for this connection"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <CertificateOfAcceptanceTab
            selectedConnectionId={selectedCOAConnectionId}
          />
        </div>
      </div>
    );
  }

  // If viewing system config (read-only), render SystemConfigView
  if (viewingSystemConfig && selectedSystemConfigConnectionId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <ViewHeader
          onBack={handleBackFromSystemConfig}
          title="System Configuration"
          subtitle="Read-only view of the saved system configuration"
        />
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <SystemConfigView
            selectedConnectionId={selectedSystemConfigConnectionId}
          />
        </div>
      </div>
    );
  }
  // If viewing logs, render the logs tab
if (viewingLogs && selectedLogsConnectionId) {
  return (
    <div className="max-w-6xl mx-auto p-6">
      <ViewHeader
        onBack={handleBackFromLogs}
        title="Activity Logs"
        subtitle="Activity history for this connection"
      />
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <LogsTab selectedConnectionId={selectedLogsConnectionId} />
      </div>
    </div>
  );
}

  return (
   <div className="max-w-6xl mx-auto p-6">
  {/* Header */}
  <div className="mb-6">
    
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Contract Connections</h2>
    <div className="flex items-center justify-between">
      <p className="text-gray-600">Select a connection to view its contract</p>
      <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
        <Eye className="h-3.5 w-3.5 mr-1.5" />
        <span>Click anywhere on a card to view</span>
      </div>
    </div>
  </div>

  {/* Search + Sort */}
  <div className="mb-6 flex flex-col sm:flex-row gap-4">
    <div className="flex-1 relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
      <input
        type="text"
        placeholder="Search by company, salesman, MD name, or registration..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
    
    <div className="flex items-center space-x-2">
      <Filter className="h-5 w-5 text-gray-400" />
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      >
        <option value="date">Sort by Date</option>
        <option value="salesman">Sort by Salesman</option>
        <option value="company">Sort by Company</option>
      </select>
    </div>
  </div>

  {/* Connections List */}
  <div className="space-y-4">
    {currentConnections.map((connection, index) => {
      const progress = getProgressLevel(connection.id);
      const colors = getProgressColors(progress.color, progress);
      
      return (
        <div 
          key={connection.id} 
          className={`group relative overflow-hidden bg-white/90 backdrop-blur-sm border-2 ${colors.border} rounded-3xl shadow-lg hover:shadow-2xl ${colors.glow} transition-all duration-500 transform hover:-translate-y-1 cursor-pointer hover:border-blue-400 active:scale-[0.995]`}
          style={{ 
            animationDelay: `${index * 100}ms`,
            animation: `slideInUp 0.6s ease-out forwards`
          }}
          onClick={() => handleViewContract(connection)}
        >
          {/* Click hint overlay */}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20">
            <div className="flex items-center space-x-1.5 text-blue-600 text-xs font-medium bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg border border-blue-100">
              <Eye className="h-3 w-3" />
              <span>View Contract</span>
            </div>
          </div>
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl pointer-events-none"></div>
          
          {isAdmin && (  
            <div className="absolute inset-0 opacity-5">
              <div className={`w-full h-full ${colors.bg}`}></div>
            </div>
          )}
          
          <div className="p-8 pt-12 relative z-10">
            <div className="flex-1">
              <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                {/* Enhanced Left side: Company info */}
                <div className="flex items-center space-x-4">
                  <div className={`${colors.bg} p-4 rounded-2xl shadow-md group-hover:shadow-lg transition-all duration-300 border border-gray-100`}>
                    <Building className={`h-6 w-6 ${colors.icon}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
                      {connection.company_name}
                    </h3>
                    <p className="text-gray-500 font-medium">{connection.company_reg}</p>
                  </div>
                </div>

                {/* Explicit action buttons */}
                <div className="hidden sm:flex items-center flex-wrap gap-2 z-30">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewSystemConfig(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 border border-indigo-700 hover:border-indigo-800 shadow-sm hover:shadow-md text-sm"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="font-medium">System Config</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewQuote(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-all duration-200 border border-orange-600 hover:border-orange-700 shadow-sm hover:shadow-md text-sm"
                  >
                    <Send className="h-4 w-4" />
                    <span className="font-medium">Quote</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewContract(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 border border-blue-700 hover:border-blue-800 shadow-sm hover:shadow-md text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">Contract</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewRental(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-200 border border-green-700 hover:border-green-800 shadow-sm hover:shadow-md text-sm"
                  >
                    <FileSignature className="h-4 w-4" />
                    <span className="font-medium">Rental</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewRentalDoc(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl transition-all duration-200 border border-emerald-800 hover:border-emerald-900 shadow-sm hover:shadow-md text-sm"
                  >
                    <ScrollText className="h-4 w-4" />
                    <span className="font-medium">Rental Doc</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewAssignment(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all duration-200 border border-purple-700 hover:border-purple-800 shadow-sm hover:shadow-md text-sm"
                  >
                    <FileSignature className="h-4 w-4" />
                    <span className="font-medium">Assignment</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewCOA(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-all duration-200 border border-teal-700 hover:border-teal-800 shadow-sm hover:shadow-md text-sm"
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    <span className="font-medium">COA</span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewLogs(connection);
                    }}
                    className="flex items-center space-x-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-xl transition-all duration-200 border border-gray-800 hover:border-gray-900 shadow-sm hover:shadow-md text-sm"
                  >
                    <HistoryIcon className="h-4 w-4" />
                    <span className="font-medium">Logs</span>
                  </button>
                </div>
              </div>

              {/* Enhanced Middle info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm mb-6">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-medium">Managing Director</p>
                    <p className="font-bold text-gray-800">{connection.md_name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-medium">Salesman</p>
                    <p className="font-bold text-gray-800">{connection.salesman_name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-medium">Contract Date</p>
                    <p className="font-bold text-gray-800">{new Date(connection.contract_date).toLocaleDateString("en-GB")}</p>
                  </div>
                </div>
              </div>

              {/* Enhanced Footer */}
              <div className="flex items-center pt-4 border-t border-gray-100">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <Mail className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">{connection.director_email}</span>
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
      {filteredConnections.length === 0 && (
        <div className="text-center py-12">
          <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No connections found</p>
          <p className="text-gray-400">Try adjusting your search terms</p>
        </div>
      )}

      {/* 🔹 Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button 
            onClick={() => handlePageChange(currentPage - 1)} 
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button 
              key={i+1} 
              onClick={() => handlePageChange(i+1)} 
              className={`px-3 py-1 border rounded ${currentPage === i+1 ? 'bg-blue-500 text-white' : ''}`}
            >
              {i+1}
            </button>
          ))}

          <button 
            onClick={() => handlePageChange(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredConnections.length)} of {filteredConnections.length} connections
      </div>
    </div>
  );
};

export default ContractViewComponent;